/**
 * Thin API client.
 *
 * Every backend error arrives in the Simple Coffee envelope (Phase 26), so the
 * client normalises it into a typed `ApiError` the UI can render directly.
 * Authorisation is never decided here — the backend is the only authority
 * (Rules 7 and 8); the client just reports what it was told.
 */

export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    details?: Record<string, unknown>;
    request_id?: string;
  };
}

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: Record<string, unknown>;
  readonly requestId?: string;

  constructor(status: number, body: ApiErrorBody['error']) {
    super(body.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    this.details = body.details ?? {};
    this.requestId = body.request_id;
  }
}

export const API_BASE_URL: string = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

const CSRF_COOKIE = 'sc_csrftoken';
const UNSAFE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

export function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

/**
 * The CSRF token, kept in memory rather than read back from the cookie.
 *
 * Production serves `sc_csrftoken` as `HttpOnly`, which is the right posture —
 * the cookie is what the server compares the header against, and nothing on the
 * page needs to read it. So the token comes from the body of `/auth/csrf`, and
 * `readCookie` is only a fallback for a configuration that leaves it readable.
 *
 * It rotates on sign-in and sign-out, which is what `forgetCsrfToken` is for.
 */
let csrfToken: string | null = null;

interface CsrfResponse {
  csrf_token?: string;
}

/**
 * The CSRF token for this session, asked for once. Session authentication
 * without one would leave every mutation open to forgery.
 */
export async function ensureCsrfToken(): Promise<string | null> {
  if (csrfToken) {
    return csrfToken;
  }

  const response = await fetch(`${API_BASE_URL}/auth/csrf`, { credentials: 'include' });
  const body: CsrfResponse | null = await response.json().catch(() => null);
  csrfToken = body?.csrf_token ?? readCookie(CSRF_COOKIE);
  return csrfToken;
}

/** Forget the token. The next mutation asks for a fresh one. */
export function forgetCsrfToken(): void {
  csrfToken = null;
}

function isApiErrorBody(value: unknown): value is ApiErrorBody {
  if (typeof value !== 'object' || value === null || !('error' in value)) {
    return false;
  }
  const error = (value as ApiErrorBody).error;
  return typeof error === 'object' && error !== null && typeof error.code === 'string';
}

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const method = (init?.method ?? 'GET').toUpperCase();
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...((init?.headers as Record<string, string> | undefined) ?? {}),
  };

  const unsafe = UNSAFE_METHODS.has(method);
  if (unsafe) {
    const token = await ensureCsrfToken();
    if (token) {
      headers['X-CSRFToken'] = token;
    }
  }

  let response = await fetch(`${API_BASE_URL}${path}`, {
    credentials: 'include',
    ...init,
    headers,
  });

  // The token rotates on sign-in and sign-out, so a cached one can be one
  // request out of date. A 403 on a mutation means nothing ran, so asking for
  // a fresh token and trying once more is safe — and spares the user a failure
  // that is ours, not theirs.
  if (response.status === 403 && unsafe && csrfToken) {
    forgetCsrfToken();
    const token = await ensureCsrfToken();
    if (token) {
      headers['X-CSRFToken'] = token;
      response = await fetch(`${API_BASE_URL}${path}`, {
        credentials: 'include',
        ...init,
        headers,
      });
    }
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const payload: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    if (isApiErrorBody(payload)) {
      throw new ApiError(response.status, payload.error);
    }
    throw new ApiError(response.status, {
      code: 'UNEXPECTED_ERROR',
      message: 'The server returned an unexpected response.',
    });
  }

  return payload as T;
}
